import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "../BasicComponents/SidebarMenu";
import PracticeCodingButton from "../components/PracticeCodingButton";
import Footer from "../BasicComponents/Footer";
import { 
  Bars3Icon, 
  XMarkIcon, 
  CodeBracketIcon, 
  SparklesIcon, 
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  BoltIcon,
  DocumentTextIcon,
  FireIcon,
  TrophyIcon,
  ChartBarIcon,
  CommandLineIcon,
  ClockIcon,
  RocketLaunchIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClipboardDocumentListIcon
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Bar } from 'react-chartjs-2';
import { getLearningConsistency } from "../utils/consistency";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { hasTsgAiAccess, TSG_AI_URL } from "../utils/tsgAiAccess";
import PaymentReminderPopup from "../components/SkillGenBot/PaymentReminderPopup";
import { checkPaymentStatus } from "../utils/paymentStatus";
import { useSubscription } from "../Context/SubscriptionContext";
import EliteSelectionModal from "../components/EliteSelectionModal";
import { UserContext } from "./UserContext";
import { useSidebar } from "../Context/SidebarContext";
import API_BASE_URL from "../Config/config";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const MAX_POPUP_DISPLAYS = 2;

const CircularProgress = ({ percent, size = 60, strokeWidth = 5, hideLabel = false }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-purple-100"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-[#5B21B6] transition-all duration-1000 ease-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="url(#progressGradient)"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5B21B6" />
            <stop offset="100%" stopColor="#A855F7" />
          </linearGradient>
        </defs>
      </svg>
      {!hideLabel && (
        <div className="absolute flex flex-col items-center justify-center text-center">
          <span className="text-[10px] font-black text-[#111827] leading-none">{percent}%</span>
          <span className="text-[5px] font-bold text-gray-400 uppercase tracking-tighter">Complete</span>
        </div>
      )}
    </div>
  );
};

const ProgressCard = ({ title, level, progress, completed, total, lastTopic, tech, navigate }) => {
  return (
    <div className="premium-card p-5 flex flex-col gap-4 relative group transition-all hover:shadow-2xl hover:shadow-purple-100 bg-[#F9F7FF]">
      <div className="absolute top-4 right-4">
        <button className="text-gray-300 hover:text-purple-600 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col items-center gap-1.5">
        <div className="w-12 h-12 rounded-2xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center justify-center text-[#A855F7] mb-1 border border-purple-50/50">
          <BoltIcon className="h-7 w-7" />
        </div>
        <h3 className="text-xl font-black text-[#111827] tracking-tight">{title}</h3>
        <span className="px-3 py-1 bg-[#EEE7FF] text-[#5B21B6] rounded-full text-[9px] font-black uppercase tracking-widest">
          {level} • Level
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-[10px] text-gray-700 font-bold mb-3">
            You left off at <span className="text-[#5B21B6]">{lastTopic || "Introduction"}</span>
          </p>
          
          <div className="space-y-1.5">
            <div className="flex justify-between items-end">
              <span className="text-[10px] font-black text-[#111827] uppercase tracking-widest opacity-80">
                {completed} / {total} Topics
              </span>
            </div>
            <div className="h-2.5 bg-[#E5E0FF] rounded-full overflow-hidden p-0.5 shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-[#5B21B6] to-[#A855F7] rounded-full transition-all duration-1000 ease-out shadow-sm" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="ml-4 shrink-0 relative flex items-center justify-center">
          <CircularProgress percent={progress} size={70} strokeWidth={5} hideLabel={true} />
          <div className="absolute flex flex-col items-center justify-center text-center">
            <span className="text-sm font-black text-[#111827] leading-none">{progress}%</span>
            <span className="text-[7px] font-black text-gray-400 uppercase tracking-tighter">Complete</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 py-3 border-t border-purple-100/50">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-1">
            <div className="w-6 h-6 rounded-lg bg-[#F3F0FF] flex items-center justify-center text-[#5B21B6] border border-purple-50">
              <BookOpenIcon className="h-3.5 w-3.5" />
            </div>
            Next Topic
          </div>
          <p className="text-[9px] font-black text-[#111827] leading-tight line-clamp-1 pl-7">Advanced Logic</p>
          <p className="text-[8px] font-bold text-[#5B21B6] pl-7">Lesson 25</p>
        </div>
        
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-1">
            <div className="w-6 h-6 rounded-lg bg-[#F3F0FF] flex items-center justify-center text-[#5B21B6] border border-purple-50">
              <ClockIcon className="h-3.5 w-3.5" />
            </div>
            Est. Time
          </div>
          <p className="text-[9px] font-black text-[#111827] leading-tight pl-7">45 mins</p>
          <p className="text-[8px] font-bold text-gray-400 pl-7 whitespace-nowrap">To complete</p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-1">
            <div className="w-6 h-6 rounded-lg bg-[#F3F0FF] flex items-center justify-center text-[#5B21B6] border border-purple-50">
              <ChartBarIcon className="h-3.5 w-3.5" />
            </div>
            Streak
          </div>
          <p className="text-[9px] font-black text-[#111827] leading-tight pl-7">7 days</p>
          <p className="text-[8px] font-bold text-gray-400 pl-7">Keep it up!</p>
        </div>
      </div>

      <button 
        onClick={() => {
          if (tech.toLowerCase() === 'mockinterviewhome') {
            navigate('/student/mockinterviewhome');
          } else {
            navigate(`/elite-mastery?tech=${tech || 'java'}`);
          }
        }}
        className="w-full bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white py-3 rounded-xl font-black text-xs flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-200 transition-all active:scale-[0.98]"
      >
        Continue Learning
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </button>
    </div>
  );
};

const LearningAnalytics = ({ analytics }) => {
    if (!analytics) return null;
    
    const stats = [
        { label: 'Started', value: analytics.technologies?.started || 0, icon: <RocketLaunchIcon className="h-5 w-5" />, color: 'bg-blue-50 text-blue-600' },
        { label: 'In Progress', value: analytics.technologies?.in_progress || 0, icon: <ClockIcon className="h-5 w-5" />, color: 'bg-amber-50 text-amber-600' },
        { label: 'Completed', value: analytics.technologies?.completed || 0, icon: <CheckCircleIcon className="h-5 w-5" />, color: 'bg-green-50 text-green-600' },
        { label: 'MCQs Attempted', value: analytics.mcqs?.total_questions_attempted || 0, icon: <ClipboardDocumentListIcon className="h-5 w-5" />, color: 'bg-purple-50 text-purple-600' }
    ];

    return (
        <section className="mb-12">
            <h2 className="text-2xl font-black text-[#111827] uppercase tracking-tight mb-6 flex items-center gap-3">
                <ChartBarIcon className="h-8 w-8 text-[#5B21B6]" />
                Learning <span className="text-[#5B21B6]">Analytics</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                    <div key={i} className="p-6 bg-white rounded-3xl border border-purple-50 shadow-sm hover:shadow-md transition-shadow">
                        <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-4`}>
                            {stat.icon}
                        </div>
                        <p className="text-3xl font-black text-[#111827]">{stat.value}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{stat.label}</p>
                    </div>
                ))}
            </div>
        </section>
    );
};


const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { expanded, hoveredLabel } = useSidebar();
  const { subscription, eliteSelection, refreshSubscription } = useSubscription();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showEliteModal, setShowEliteModal] = useState(false);
  const [tsgAiAccess, setTsgAiAccess] = useState({ hasAccess: false });
  const [dashboardData, setDashboardData] = useState({
    reading_progress: [],
    mcq_progress: [],
    launchpad_progress: []
  });
  const [consistencyData, setConsistencyData] = useState([]);
  const [techMcqProgress, setTechMcqProgress] = useState([]);
  const [learningAnalytics, setLearningAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('weekly');

  const [lastRead, setLastRead] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [techProgress, setTechProgress] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [bootcampData, setBootcampData] = useState(null);
  const [bootcampProgress, setBootcampProgress] = useState(0);
  const [launchpadProgress, setLaunchpadProgress] = useState([]);

  const processChartData = () => {
    const labels = [];
    const data = [];
    let daysArr = 7;
    
    // Ensure consistencyData is an array to prevent "find is not a function" error
    const safeConsistencyData = Array.isArray(consistencyData) ? consistencyData : [];
    
    if (viewMode === 'weekly') daysArr = 7;
    else if (viewMode === 'monthly') daysArr = 30;
    else if (viewMode === 'daily') daysArr = 1;
    
    if (viewMode === 'daily') {
        // Show hours of the day
        for (let i = 0; i < 24; i++) {
            labels.push(`${i}:00`);
            data.push(0); 
        }
        const today = new Date().toISOString().split('T')[0];
        const activity = safeConsistencyData.find(c => {
            const cDate = new Date(c.activity_date);
            return cDate.toISOString().split('T')[0] === today;
        });
        if (activity) {
            const currentHour = new Date().getHours();
            data[currentHour] = activity.activity_count;
        }
    } else {
        for (let i = daysArr - 1; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            const activity = safeConsistencyData.find(c => {
                const cDate = new Date(c.activity_date);
                return cDate.toISOString().split('T')[0] === dateStr;
            });
            
            labels.push(d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }));
            data.push(activity ? activity.activity_count : 0);
        }
    }
    
    return {
      labels,
      datasets: [{
        label: 'Learning Activities',
        data,
        backgroundColor: '#5B21B6',
        borderRadius: 8,
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1E1B4B',
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        padding: 16,
        displayColors: false,
        borderRadius: 12,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 }
      },
      x: {
        grid: { display: false }
      }
    }
  };

  useEffect(() => {
    const userId = user?.uid || user?.id || localStorage.getItem("userId");
    if (userId) {
      const fetchData = async () => {
        try {
          const [dashResponse] = await Promise.all([
            fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
              headers: { 'user-id': userId }
            })
          ]);
          
          if (dashResponse.ok) {
            const data = await dashResponse.json();
            setDashboardData(data);
            
            if (data.consistency) {
              setConsistencyData(Array.isArray(data.consistency) ? data.consistency : []);
            }
            
            // Normalize progress data
            if (data.progress) {
              setRecentActivity(data.progress);
              
              // Map to categories
              const techMcq = data.progress.filter(p => p.module === 'TechMCQ' || (p.module_type === 'MCQ' && p.module !== 'Launchpad'));
              const launchpad = data.progress.filter(p => p.module === 'Launchpad');
              const reading = data.progress.filter(p => p.module_type === 'Reading');
              
              setTechMcqProgress(techMcq);
              setLaunchpadProgress(launchpad);
              setVisitedCourses(reading);
            }
          }
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [user]);

  const handleContinueLearning = (item) => {
    if (item.module_type === 'Reading') {
        const isTechnologyExplorer = ['AI', 'AWS', 'React', 'ai', 'aws', 'react'].includes(item.technology) || 
                                     ['AI', 'AWS', 'React', 'ai', 'aws', 'react'].includes(item.technology?.toLowerCase()) ||
                                     item.module === 'TechDocs';
        
        if (isTechnologyExplorer) {
            navigate(`/document/${item.technology}?file=${encodeURIComponent(item.file_path)}&scroll=${item.scroll_position || 0}`);
        } else {
            // Elite Masteries
            navigate(`/elite-mastery/${item.technology}?file=${encodeURIComponent(item.file_path)}&scroll=${item.scroll_position || 0}`);
        }
    } else if (item.module_type === 'MCQ') {
        navigate(`/techmcq/${item.technology}/${(item.current_level || 0)}`);
    } else if (item.module_type === 'Launchpad') {
        navigate('/launchpadpage');
    }
  };

  const [recentTechs, setRecentTechs] = useState([]);
  const [visitedCourses, setVisitedCourses] = useState([]);
  const [learningActivity, setLearningActivity] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [totalCompletedTopics, setTotalCompletedTopics] = useState(0);
  const [learningStats, setLearningStats] = useState({
    hours: 0,
    topics: 0,
    streak: 0,
    xp: 0,
    compilerRuns: 0,
    aiSessions: 0,
    mockInterviews: 0
  });

  useEffect(() => {
    const saved = localStorage.getItem('last_read_doc');
    if (saved) {
      setLastRead(JSON.parse(saved));
    }

    const activities = JSON.parse(localStorage.getItem('user_activity') || "[]");
    setRecentActivity(activities);
    
    // Map local activities to the format expected by processChartData
    const localConsistency = activities.reduce((acc, curr) => {
      const date = new Date(curr.time).toISOString().split('T')[0];
      const existing = acc.find(a => a.activity_date === date);
      if (existing) {
        existing.activity_count++;
      } else {
        acc.push({ activity_date: date, activity_count: 1 });
      }
      return acc;
    }, []);
    setConsistencyData(localConsistency);

    const loadDynamicData = async () => {
      // 1. Fetch Tech Explorer Progress (using contentMap for total counts)
      try {
        const response = await fetch('/contentMap.json');
        const data = await response.json();
        const contentMapData = Array.isArray(data) ? data : [];
        
        const techs = ['AI', 'AWS', 'React', 'Java', 'Python', 'java-premium', 'python-premium'];
        const progressMap = {};
        let totalCompleted = 0;
        
        // Find which techs have ANY interaction (either last read, activity, or completed docs)
        const activeTechs = techs.filter(tech => {
          const completed = JSON.parse(localStorage.getItem(`completed_docs_${tech}`) || "[]");
          const hasActivity = activities.some(a => a.tech === tech);
          const isLastRead = lastRead?.tech === tech;
          return completed.length > 0 || hasActivity || isLastRead;
        });

        activeTechs.forEach(tech => {
          const completedKey = `completed_docs_${tech}`;
          const completed = JSON.parse(localStorage.getItem(completedKey) || "[]");
          totalCompleted += completed.length;
          
          let techSearchName = tech;
          if (tech === 'java-premium') techSearchName = 'java';
          if (tech === 'python-premium') techSearchName = 'python';

          let techData = contentMapData.find(item => 
            item.technology?.toLowerCase() === techSearchName.toLowerCase()
          );

          if (!techData && tech.includes('-premium')) {
             techData = contentMapData.find(item => 
               item.technology?.toLowerCase().includes('elite') && 
               item.technology?.toLowerCase().includes(techSearchName)
             );
          }
          
          let totalDocs = 0;
          let techDocs = [];
          if (techData && techData.topics) {
            techData.topics.forEach(topic => {
              if (topic.subtopics) {
                totalDocs += topic.subtopics.length;
                techDocs.push(...topic.subtopics);
              }
            });
          }
          
          progressMap[tech] = {
            completed: completed.length,
            total: totalDocs || 1,
            percent: Math.round((completed.length / (totalDocs || 1)) * 100),
            docs: techDocs
          };
        });
        
        // Add Elite Techs (Premium)
        const eliteTechs = ['java-premium', 'python-premium'];
        for (const etech of eliteTechs) {
          const completed = JSON.parse(localStorage.getItem(`completed_docs_${etech}`) || "[]");
          totalCompleted += completed.length;
          try {
            const mres = await fetch(`/docs/${etech}/mapping.json`);
            if (mres.ok) {
              const mapping = await mres.json();
              const total = Object.keys(mapping).length;
              progressMap[etech] = {
                completed: completed.length,
                total: total,
                percent: Math.round((completed.length / (total || 1)) * 100)
              };
            }
          } catch (e) {}
        }
        
        setTechProgress(Object.fromEntries(
          Object.entries(progressMap).filter(([_, data]) => data.percent > 0 && data.percent < 100)
        ));
        setTotalCompletedTopics(totalCompleted);

        // --- DYNAMIC RECOMMENDATIONS ---
        const recommended = [];
        
        // 1. Suggest continuing the most progressed tech (that isn't 100% finished)
        const sortedTechs = Object.entries(progressMap)
          .filter(([_, data]) => data.percent < 100)
          .sort((a, b) => b[1].percent - a[1].percent);
        
        if (sortedTechs.length > 0) {
          const [topTech, data] = sortedTechs[0];
          recommended.push({
            title: `Continue ${topTech.charAt(0).toUpperCase() + topTech.slice(1)}`,
            level: data.percent > 50 ? 'Advanced' : 'Intermediate',
            icon: <BoltIcon />,
            path: topTech.includes('premium') || ['java', 'python', 'cpp', 'c', 'javascript', 'html', 'css', 'sql', 'dsa'].includes(topTech.toLowerCase())
              ? `/elite-mastery?tech=${topTech}` 
              : `/explore/${topTech.toLowerCase().replace(/\s+/g, '-')}`
          });
        }

        // 2. Suggest Interview Prep if user has completed some topics
        if (totalCompleted > 5) {
            recommended.push({
                title: 'Interview Preparation',
                level: 'Career',
                icon: <BriefcaseIcon />,
                path: '/student/mockinterviewhome'
            });
        }
        
        // 3. Suggest a new tech if any are at 0%
        const unstarted = Object.entries(progressMap).filter(([_, data]) => data.completed === 0);
        if (unstarted.length > 0) {
          const [newTech] = unstarted[0];
          recommended.push({
            title: `Start ${newTech.charAt(0).toUpperCase() + newTech.slice(1)}`,
            level: 'Beginner',
            icon: <SparklesIcon />,
            path: newTech.includes('premium') || ['java', 'python', 'cpp', 'c', 'javascript', 'html', 'css', 'sql', 'dsa'].includes(newTech.toLowerCase())
              ? `/elite-mastery?tech=${newTech}` 
              : `/explore/${newTech.toLowerCase().replace(/\s+/g, '-')}`
          });
        }

        // 4. AI Suggested Next Learning (Dynamic based on current progress)
        if (sortedTechs.length > 0) {
           const [topTech] = sortedTechs[0];
           recommended.push({
             title: `Master ${topTech.charAt(0).toUpperCase() + topTech.slice(1)}`,
             level: 'Recommended',
             icon: <SparklesIcon />,
             path: topTech.includes('premium') || ['java', 'python', 'cpp', 'c', 'javascript', 'html', 'css', 'sql', 'dsa'].includes(topTech.toLowerCase())
                ? `/elite-mastery?tech=${topTech}` 
                : `/explore/${topTech.toLowerCase().replace(/\s+/g, '-')}`
           });
        }
        
        setRecommendations(recommended.slice(0, 4));

        // Recently Visited and Recent Techs
        const recent = activities.slice(0, 5).map(a => a.tech).filter((v, i, a) => a.indexOf(v) === i);
        setRecentTechs(recent);
        setVisitedCourses(activities.slice(0, 4));

        // Total Hours (Mock calculation based on activities)
        setTotalHours(Math.round(activities.length * 0.5));

        // Update Stats
        setLearningStats({
          topics: totalCompleted,
          hours: Math.round(activities.length * 0.5),
          streak: Math.min(activities.length, 5) || 1,
          xp: totalCompleted * 50 + (activities.length * 10),
          compilerRuns: activities.filter(a => a.type === 'compiler').length || 12,
          aiSessions: activities.filter(act => act.event?.includes('AI')).length || 8,
          mockInterviews: activities.filter(act => act.event?.includes('Interview')).length || 2
        });

      } catch (err) {
        console.error("Failed to load content metrics:", err);
      }

      // 2. Fetch Bootcamp Progress
      const userId = user?.userId || user?.id || localStorage.getItem("user_id");
      if (userId) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/my-bootcamps?userId=${userId}`);
          const data = await res.json();
          if (data && data.enrollments && data.enrollments.length > 0) {
            setBootcampData(data.enrollments[0]);
            const totalPct = data.enrollments.reduce((sum, en) => sum + (en.progressPercentage || 0), 0);
            setBootcampProgress(Math.round(totalPct / data.enrollments.length));
          }
        } catch (e) {}

        // Fetch Launchpad Progress
        try {
          const trackMapping = {
            'Java Backend': 'full_stack_questions',
            'Full Stack Development': 'full_stack_questions',
            'Data Science & AI': 'data_science_questions',
            'Cloud Computing & DevOps': 'cloud_computing_questions',
            'Cyber Security': 'cyber_security_questions',
            'Mobile App Development': 'mobile_app_questions',
            'Software Testing & QA': 'software_testing_questions'
          };

          const trackProgressPromises = Object.entries(trackMapping).map(async ([trackName, tableName]) => {
            try {
              // Note: This is an estimation. Real subtopics are fetched per track in SkillgenLaunchpadLevels
              const subtopics = ["JPA", "Hibernate", "Spring Core", "Express.js", "MongoDB", "React.js"]; 
              
              const subtopicPromises = subtopics.map(async (sub) => {
                try {
                  const res = await fetch(`${API_BASE_URL}/sgl/passed-levels/${userId}/${tableName}/${encodeURIComponent(sub)}`);
                  if (res.ok) {
                    const data = await res.json();
                    return data.passedLevels ? data.passedLevels.length : 0;
                  }
                } catch (e) {}
                return 0;
              });

              const subtopicResults = await Promise.all(subtopicPromises);
              const totalPassed = subtopicResults.reduce((a, b) => a + b, 0);

              if (totalPassed > 0) {
                return {
                  name: trackName,
                  passed: totalPassed,
                  total: subtopics.length * 20,
                  percent: Math.min(100, Math.round((totalPassed / (subtopics.length * 20)) * 100))
                };
              }
            } catch (e) {}
            return null;
          });

          const launchpadResults = await Promise.all(trackProgressPromises);
          // Only show in-progress items (percent > 0 and < 100)
          setLaunchpadProgress(launchpadResults.filter(p => p !== null && p.percent > 0 && p.percent < 100));

          // Fetch Technology MCQ Progress independently
          try {
            const scoresRes = await fetch(`${API_BASE_URL}/user-scores-badges/${userId}`);
            if (scoresRes.ok) {
              const scoresData = await scoresRes.json();
              const techMcqs = scoresData.scores.filter(s => s.type === 'Technology MCQ' && s.levels > 0);
              
              const techResults = techMcqs.map(item => ({
                name: item.name,
                passed: item.levels,
                total: 20,
                percent: Math.min(100, Math.round((item.levels / 20) * 100))
              }));

              // Show both in-progress and completed for better UX, or just in-progress as before
              // The user wants to see it after passing a level, so we show it if passed > 0
              setTechMcqProgress(techResults);
            }
          } catch (e) {
            console.error("Failed to fetch tech mcq progress via scores:", e);
          }
        } catch (e) {
          console.error("Failed to fetch launchpad progress:", e);
        }
      }
    };

    loadDynamicData();
  }, [user]);
  const popupDisplayCountRef = useRef(0);
  const sessionStartTimeRef = useRef(null);

  const studentName = user?.name || user?.first_name || user?.username || "Learner";

  // Personalized Welcome Message Components
  const currentTech = lastRead?.tech || 'Full Stack Web Development';
  const completedTechCount = Object.values(techProgress).filter(t => t.percent === 100).length;
  const recommendedNext = recommendations[0]?.title || recommendations[0]?.name || 'Node.js';

  const quickAccess = [
    { title: 'AI Tutor', path: '/tsg', icon: <SparklesIcon className="h-6 w-6" />, color: '#5B21B6' },
    { title: 'Premium Interview', path: '/premium-interview', icon: <BriefcaseIcon className="h-6 w-6" />, color: '#A855F7' },
    { title: 'Free Discount Interview', path: '/discount-interview', icon: <SparklesIcon className="h-6 w-6" />, color: '#F59E0B' },
    { title: 'Compiler', path: '/compiler', icon: <CodeBracketIcon className="h-6 w-6" />, color: '#7C3AED' },
    { title: 'Tech Mastery', path: '/techgrid', icon: <BoltIcon className="h-6 w-6" />, color: '#10B981' },
    { title: 'Bootcamp', path: '/bootcamp/my-bootcamps', icon: <AcademicCapIcon className="h-6 w-6" />, color: '#4F46E5' },
    { title: 'Assessments', path: '/skillgenlaunchpadlevels', icon: <DocumentTextIcon className="h-6 w-6" />, color: '#D97706' },
  ];

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [mobileOpen]);

  useEffect(() => {
    const checkElite = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const isCollegeStudent = storedUser.role === 'student' && (storedUser.tenant || storedUser.college_id);
      if (!subscription.loading && !subscription.isPaid && !eliteSelection && !isCollegeStudent) {
        setShowEliteModal(true);
      }
    };
    checkElite();
  }, [subscription, eliteSelection]);

  useEffect(() => {
    const checkAccess = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = storedUser.userId || storedUser.id || localStorage.getItem("user_id");
      if (userId) {
        const access = await hasTsgAiAccess(userId);
        setTsgAiAccess(access);
      }
    };
    checkAccess();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#111827] font-sans relative flex flex-col">
      <EliteSelectionModal 
        isOpen={showEliteModal} 
        onClose={() => setShowEliteModal(false)}
        userId={localStorage.getItem('user_id')}
        onSelectionSuccess={() => refreshSubscription()}
      />

      <div className="flex flex-1">
        <aside className="hidden md:block shrink-0 z-30">
          <SidebarMenu />
        </aside>

        <div className={`flex-1 flex flex-col ${expanded ? "md:ml-64" : "md:ml-20"} transition-all duration-300 min-h-screen`}>
          <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto w-full space-y-8">
            
            {/* Mobile Header Toggle */}
            <div className="md:hidden flex items-center justify-between mb-4">
              <button onClick={() => setMobileOpen(true)} className="p-2 bg-white rounded-xl shadow-sm border border-[#E5E7EB]">
                <Bars3Icon className="h-6 w-6 text-[#5B21B6]" />
              </button>
              <div className="font-bold text-[#5B21B6] text-lg">Skill Genie</div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#5B21B6] to-[#A855F7] p-0.5 shadow-md">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${studentName}`} alt="Profile" className="w-full h-full rounded-full bg-white" />
              </div>
            </div>

            {/* Hero Section */}
            <section className="animate-fade-in relative">
              {/* Hover Label Indicator */}
              {!expanded && hoveredLabel && (
                <div 
                  className="fixed glass-panel px-4 py-2 rounded-xl animate-slide-right flex items-center gap-2 z-[9999] pointer-events-none border-purple-100"
                  style={{ 
                    top: `${hoveredLabel.top}px`, 
                    left: '88px',
                    transform: 'translateY(-50%)' 
                  }}
                >
                  <SparklesIcon className="h-4 w-4 text-[#5B21B6] animate-pulse" />
                  <span className="text-sm font-black text-[#5B21B6] uppercase tracking-widest whitespace-nowrap">
                    {hoveredLabel.text}
                  </span>
                </div>
              )}
              
              <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#1E1B4B] via-[#5B21B6] to-[#7C3AED] p-6 sm:p-8 text-white shadow-[0_20px_40px_rgba(30,27,75,0.2)] group">
                <div className="relative z-10 flex flex-col md:flex-row items-start justify-between gap-8">
                  <div className="flex-1 space-y-4">
                    <div className="space-y-2 text-left">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/10 mb-1 -ml-1">
                        <FireIcon className="h-3 w-3 text-orange-400" />
                        <span className="text-[7px] font-black uppercase tracking-[0.2em]">Learning Streak: {learningStats.streak} Days</span>
                      </div>
                      <h1 className="text-xl sm:text-3xl font-black tracking-tight leading-tight block text-white -ml-1">
                        Hi <span className="text-white">{studentName}</span> 👋
                      </h1>
                      <p className="text-sm text-purple-100/70 font-medium max-w-lg">
                        Ready to master new skills today? Your progress is looking impressive.
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      <button 
                        onClick={() => {
                          if (lastRead?.file) {
                            const isPremium = lastRead.tech?.toLowerCase().includes('premium');
                            navigate(isPremium ? `/elite-mastery?tech=${lastRead.tech}` : `/topic-explorer?tech=${lastRead.tech}`);
                          } else {
                            navigate('/dashboard');
                          }
                        }}
                        className="bg-white text-[#1E1B4B] px-6 py-2.5 rounded-xl font-black hover:scale-105 hover:shadow-[0_15px_30px_rgba(255,255,255,0.2)] transition-all flex items-center gap-2 shadow-lg active:scale-95 group/btn text-[10px]"
                      >
                        <RocketLaunchIcon className="h-4 w-4 group-hover/btn:animate-bounce" />
                        {lastRead?.file ? 'Resume Journey' : 'Start Learning'}
                      </button>
                      <button onClick={() => navigate('/tsg')} className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-2.5 rounded-xl font-black hover:bg-white/20 transition-all flex items-center gap-2 active:scale-95 text-[10px]">
                        <SparklesIcon className="h-4 w-4" />
                        AI Tutor
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex-1 w-full lg:max-w-md animate-fade-in" style={{ animationDelay: '200ms' }}>
                    <div className="relative group/panel">
                      {/* Glassmorphism Card */}
                      <div className="relative z-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 shadow-2xl transition-all duration-500 hover:bg-white/15 hover:shadow-purple-500/20 hover:-translate-y-1">
                        <div className="space-y-3">
                          <div>
                            <h3 className="text-base font-black tracking-tight text-white mb-0.5">Today's Focus</h3>
                            <p className="text-[10px] text-purple-100/70 font-medium">Continue your learning journey and build momentum.</p>
                          </div>

                          <div className="grid gap-2">
                            {[
                              { icon: BookOpenIcon, title: "Continue Learning", desc: "Pick up where you left off and maintain consistency." },
                              { icon: ClipboardDocumentListIcon, title: "Complete Assessments", desc: "Validate your understanding through guided evaluations." },
                              { icon: CodeBracketIcon, title: "Practice Coding", desc: "Strengthen practical skills through hands-on exercises." },
                              { icon: BriefcaseIcon, title: "Prepare for Interviews", desc: "Build confidence with structured interview preparation." }
                            ].map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2.5 group/item cursor-pointer">
                                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center border border-white/10 group-hover/item:bg-white/20 transition-colors">
                                  <item.icon className="h-3.5 w-3.5 text-purple-200" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <CheckCircleIcon className="h-3 w-3 text-emerald-400" />
                                    <span className="text-[10px] font-bold text-white group-hover/item:text-purple-200 transition-colors">{item.title}</span>
                                  </div>
                                  <p className="text-[8px] text-purple-100/50">{item.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="pt-2 border-t border-white/10">
                            <p className="text-[8px] text-purple-100/60 font-medium italic leading-relaxed">
                              "Every small step compounds into long-term career growth."
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Decorative Glow */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-[32px] blur-2xl opacity-0 group-hover/panel:opacity-100 transition-opacity duration-500 -z-10"></div>
                    </div>
                  </div>
                </div>
                
                {/* Abstract shapes */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>
              </div>
            </section>

            <div className="grid grid-cols-1 gap-8">
              <div className="space-y-8 animate-slide-up" style={{ animationDelay: '100ms' }}>
                
                {/* Consistency Section */}
                <section className="interactive-card p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
                    <div>
                      <h2 className="text-xl font-black text-[#111827] flex items-center gap-2">
                        <ChartBarIcon className="h-6 w-6 text-[#5B21B6]" />
                        Learning Consistency
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">Your activity trends over time</p>
                    </div>
                    <div className="flex bg-purple-50 p-1 rounded-xl shadow-inner">
                      <button 
                        onClick={() => setViewMode('daily')}
                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'daily' ? 'bg-white text-[#5B21B6] shadow-sm' : 'text-gray-400 hover:text-[#5B21B6]'}`}
                      >
                        Daily
                      </button>
                      <button 
                        onClick={() => setViewMode('weekly')}
                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'weekly' ? 'bg-white text-[#5B21B6] shadow-sm' : 'text-gray-400 hover:text-[#5B21B6]'}`}
                      >
                        Weekly
                      </button>
                      <button 
                        onClick={() => setViewMode('monthly')}
                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'monthly' ? 'bg-white text-[#5B21B6] shadow-sm' : 'text-gray-400 hover:text-[#5B21B6]'}`}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>
                  
                  <div className="h-[300px] w-full bg-white rounded-[24px] p-6 shadow-inner border border-purple-50 relative overflow-hidden">
                    {/* Background decorative grid */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#5B21B6 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                    
                    {consistencyData ? (
                      <Bar 
                        data={processChartData()} 
                        options={chartOptions} 
                      />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center gap-4">
                        <div className="w-12 h-12 border-4 border-purple-100 border-t-[#5B21B6] rounded-full animate-spin"></div>
                        <p className="text-gray-400 font-black uppercase text-[10px] tracking-widest">Analyzing your data...</p>
                      </div>
                    )}
                  </div>
                </section>

        {/* Recommended for You */}
        <section className="space-y-6">
           <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-[#5B21B6] to-[#A855F7] rounded-full"></div>
              <h2 className="text-xl font-black text-[#111827]">Recommended for You</h2>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {recommendations.length > 0 ? recommendations.map((rec, i) => (
                <ProgressCard
                  key={i}
                  title={rec.title}
                  level={rec.level}
                  progress={0}
                  completed={0}
                  total={10}
                  tech={rec.path.split('tech=')[1] || ''}
                  navigate={navigate}
                />
              )) : (
                [
                  { title: 'AI Foundations', level: 'Beginner', path: '/elite-mastery?tech=ai' },
                  { title: 'Java Elite', level: 'Advanced', path: '/elite-mastery?tech=java' },
                  { title: 'Python Mastery', level: 'Intermediate', path: '/elite-mastery?tech=python' },
                  { title: 'Mock Interviews', level: 'Career', path: '/student/mockinterviewhome' },
                ].map((rec, i) => (
                  <ProgressCard
                    key={i}
                    title={rec.title}
                    level={rec.level}
                    progress={0}
                    completed={0}
                    total={10}
                    tech={rec.path.split('tech=')[1] || ''}
                    navigate={navigate}
                  />
                ))
              )}
           </div>
        </section>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] animate-fade-in">
           <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
           <aside className="absolute left-0 top-0 h-full w-[280px] bg-white shadow-2xl animate-slide-right">
              <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 p-2 text-[#6B7280]">
                <XMarkIcon className="h-6 w-6" />
              </button>
              <SidebarMenu variant="mobile" onRequestClose={() => setMobileOpen(false)} />
           </aside>
        </div>
      )}
    </div>
  );
};

export default Dashboard;


